/**
 * Предпросмотр раскладки — главный экран автосбора (AK-791, SRS FR-8…FR-10).
 *
 * Показываем ДО записи, потому что автосбор трогает сотни строк разом: если бы
 * он писал сразу, «отменить» значило бы вручную вычистить семьсот полей.
 * Отсюда порядок блоков — от общего к частному:
 *   1) четыре числа «что получилось»,
 *   2) цель против раскладки по культурам,
 *   3) что НЕ разложилось и почему — сгруппировано и по-человечески,
 *   4) «почему так?» по конкретному полю.
 *
 * Причины отказа сервер отдаёт уже текстом; экран их не сочиняет и кодов
 * (`norm_violation` и прочих) не показывает — они нужны только для
 * группировки.
 */
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Stack } from '@/components/ui/stack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { reasonGroupLabel, type AutoBuildPreview } from '@/lib/planning/autoBuild';
import { formatHa, formatMln } from '@/lib/planning/planVariant';

/** Сколько строк показываем сразу: 800 полей в DOM разом — это подвисший экран. */
const CHUNK = 25;

/** Якорь блока правил на экране посевного плана — RotationRulesPanel. */
const ROTATION_RULES_ANCHOR = 'rotation-rules-panel';

/**
 * Один блок вместо повторения одной и той же причины на каждое поле
 * (AK-809, живой QA-кейс «Заречного»: свежая копия варианта БЕЗ правил
 * севооборота даёт «0 из Y» с 1052 одинаковыми строками `no_compatible_crop`
 * — раскладка гарантированно пустая, и объяснять её ПОЛЕ ЗА ПОЛЕМ не о чем).
 * Используется и ДО расчёта (в форме цели), и В предпросмотре — один и тот
 * же текст в обоих местах, чтобы агроном не читал два разных объяснения одной
 * причины.
 */
export function NoRulesHint() {
  return (
    <div
      role="alert"
      className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
    >
      <p>
        В этом варианте плана нет ни одного правила севооборота — системе не по чему судить,
        какую культуру можно сеять после какой, и разложить план она не сможет ни на одном
        поле. Обычно так бывает у свежей копии варианта: правила севооборота копия не
        наследует, их нужно завести заново.
      </p>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="mt-2 border-destructive/40 text-destructive hover:bg-destructive/10"
        onClick={() => {
          document
            .getElementById(ROTATION_RULES_ANCHOR)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      >
        Перейти к правилам севооборота
      </Button>
    </div>
  );
}

function Tile({
  label,
  value,
  note,
  tone = 'plain',
}: {
  label: string;
  value: string;
  note: string;
  tone?: 'plain' | 'good' | 'alert';
}) {
  return (
    <div
      className={
        tone === 'alert'
          ? 'rounded-lg border border-destructive/40 bg-destructive/10 p-3'
          : 'rounded-lg border border-border bg-muted/40 p-3'
      }
    >
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={
          tone === 'good'
            ? 'mt-1 text-xl font-semibold text-primary'
            : tone === 'alert'
              ? 'mt-1 text-xl font-semibold text-destructive'
              : 'mt-1 text-xl font-semibold text-foreground'
        }
      >
        {value}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{note}</div>
    </div>
  );
}

function deviationText(value: number): string {
  if (Math.abs(value) < 0.5) return '0';
  return `${value > 0 ? '+' : '−'}${formatHa(Math.abs(value))}`;
}

export function AutoBuildPreviewPanel({
  preview,
  applying,
  staleMessage,
  onApply,
  onEditTargets,
  onRebuild,
  onCancel,
}: {
  preview: AutoBuildPreview;
  applying: boolean;
  /** Текст конфликта «данные изменились» — рядом с кнопкой, а не тостом. */
  staleMessage: string | null;
  onApply: () => void;
  onEditTargets: () => void;
  onRebuild: () => void;
  onCancel: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [shownAssigned, setShownAssigned] = useState(CHUNK);
  const [shownUnresolved, setShownUnresolved] = useState(CHUNK);
  const t = preview.totals;

  return (
    <Stack gap={4}>
      <div className="rounded-md border border-primary/40 bg-primary/5 p-3 text-sm">
        Разложено <span className="font-medium">{t.assigned_fields}</span> полей —{' '}
        <span className="font-medium">{formatHa(t.assigned_ha)}</span>. В плане пока ничего
        не изменилось: строки появятся только после «Записать в черновик».
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Tile
          label="Разложено"
          value={formatHa(t.assigned_ha)}
          note={`${t.assigned_fields} полей · ${
            preview.arable_ha > 0
              ? `${((t.assigned_ha / preview.arable_ha) * 100).toFixed(1)} % пашни`
              : 'пашня не задана'
          }`}
          tone="good"
        />
        <Tile
          label="Отклонение от цели"
          value={deviationText(t.target_deviation_ha)}
          note="суммарно по всем культурам"
        />
        <Tile
          label="Плановая выручка"
          value={t.revenue_rub === null ? 'нет доступа' : formatMln(t.revenue_rub)}
          note={
            t.revenue_rub === null
              ? 'колонка под правом на бюджетные справочники'
              : (preview.revenue_note ?? 'по плановой урожайности и плановым ценам')
          }
        />
        <Tile
          label="Не разложилось"
          value={String(t.conflicts)}
          note="полей, которым культуру не подобрали"
          tone={t.conflicts > 0 ? 'alert' : 'plain'}
        />
      </div>

      <div>
        <h4 className="mb-2 text-sm font-semibold">Цель против раскладки</h4>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Культура</TableHead>
                <TableHead className="text-right">Цель</TableHead>
                <TableHead className="text-right">Разложено</TableHead>
                <TableHead className="text-right">Отклонение</TableHead>
                <TableHead className="text-right">Полей</TableHead>
                <TableHead className="text-right">Урожайность</TableHead>
                {t.revenue_rub !== null && (
                  <TableHead className="text-right">Плановая выручка</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.by_crop.map((row) => (
                <TableRow key={row.crop_id}>
                  <TableCell className="font-medium">
                    {row.crop_name ?? 'культура без названия'}
                  </TableCell>
                  <TableCell className="text-right">{formatHa(row.target_ha)}</TableCell>
                  <TableCell className="text-right">{formatHa(row.assigned_ha)}</TableCell>
                  <TableCell className="text-right">{deviationText(row.deviation_ha)}</TableCell>
                  <TableCell className="text-right">{row.fields}</TableCell>
                  <TableCell className="text-right">
                    {row.avg_yield_c_ha === null
                      ? '—'
                      : `${row.avg_yield_c_ha.toFixed(0)} ц/га`}
                  </TableCell>
                  {t.revenue_rub !== null && (
                    <TableCell className="text-right">
                      {row.revenue_rub === null ? '—' : formatMln(row.revenue_rub)}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {preview.unresolved.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold">
            Не разложилось: {preview.unresolved.length} полей
          </h4>
          {!preview.has_rules ? (
            // Причина ОДНА на весь прогон, а не на поле — сотни одинаковых
            // строк с одним и тем же текстом только прячут единственный
            // осмысленный факт («правил нет») в шуме.
            <NoRulesHint />
          ) : (
            <>
              <div className="mb-2 flex flex-wrap gap-2">
                {preview.reason_groups.map((group) => (
                  <Badge key={group.reason} variant="outline">
                    {reasonGroupLabel(group.reason)} — {group.fields} полей ·{' '}
                    {formatHa(group.area_ha)}
                  </Badge>
                ))}
              </div>
              <ul className="divide-y divide-border rounded-md border border-border">
                {preview.unresolved.slice(0, shownUnresolved).map((item) => (
                  <li key={item.field_id} className="flex flex-wrap gap-x-3 gap-y-1 p-3 text-sm">
                    <span className="font-medium">{item.field_name}</span>
                    <span className="text-muted-foreground">{formatHa(item.area_ha)}</span>
                    <span className="w-full text-muted-foreground sm:w-auto sm:flex-1">
                      {item.message}
                    </span>
                  </li>
                ))}
              </ul>
              {preview.unresolved.length > shownUnresolved && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setShownUnresolved((n) => n + CHUNK)}
                >
                  Показать ещё ({preview.unresolved.length - shownUnresolved})
                </Button>
              )}
            </>
          )}
        </div>
      )}

      <div>
        <h4 className="mb-2 text-sm font-semibold">Что куда легло</h4>
        <ul className="divide-y divide-border rounded-md border border-border">
          {preview.assigned.slice(0, shownAssigned).map((item) => (
            <li key={item.field_id} className="p-3 text-sm">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-medium">{item.field_name}</span>
                <span className="text-muted-foreground">{formatHa(item.area_ha)}</span>
                <span>{item.crop_name ?? 'культура без названия'}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="ml-auto"
                  aria-expanded={expanded === item.field_id}
                  onClick={() =>
                    setExpanded((cur) => (cur === item.field_id ? null : item.field_id))
                  }
                >
                  почему так?
                </Button>
              </div>
              {expanded === item.field_id && (
                <p className="mt-2 border-l-2 border-primary/50 pl-3 text-sm text-muted-foreground">
                  {item.explanation}
                </p>
              )}
            </li>
          ))}
        </ul>
        {preview.assigned.length > shownAssigned && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setShownAssigned((n) => n + CHUNK)}
          >
            Показать ещё ({preview.assigned.length - shownAssigned})
          </Button>
        )}
      </div>

      {staleMessage && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {staleMessage}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <Button
          type="button"
          disabled={applying || t.assigned_fields === 0}
          onClick={() => setConfirm(true)}
        >
          Записать в черновик ({t.assigned_fields})
        </Button>
        <Button type="button" variant="outline" disabled={applying} onClick={onEditTargets}>
          Изменить цель
        </Button>
        <Button type="button" variant="outline" disabled={applying} onClick={onRebuild}>
          Пересобрать
        </Button>
        <Button type="button" variant="ghost" disabled={applying} onClick={onCancel}>
          Отменить
        </Button>
      </div>

      <Dialog open={confirm} onOpenChange={(next) => !applying && setConfirm(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Записать раскладку в черновик?</DialogTitle>
            <DialogDescription>
              Это правка черновика — вариант не утверждается и на рабочий бюджет не
              влияет.
            </DialogDescription>
          </DialogHeader>
          {/* Диалог говорит и что произойдёт, и чего НЕ произойдёт (FR-14):
              страх «затрёт мою работу» снимается только вторым списком. */}
          <ul className="space-y-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
            <li>
              В <span className="font-medium">{t.assigned_fields}</span> полей будет
              проставлена культура и плановая урожайность по нормативу.
            </li>
            <li>
              Эти строки получат пометку{' '}
              <span className="font-medium">«собрано автоматически»</span> — вы всегда
              отличите их от своих.
            </li>
            <li className="text-muted-foreground">
              {t.manual_untouched_fields} полей, назначенных вручную, не тронем — ни
              культуру, ни урожайность.
            </li>
            <li className="text-muted-foreground">
              {t.conflicts} полей, где культуру подобрать не удалось, останутся пустыми.
            </li>
            <li className="text-muted-foreground">
              Пар автосбор не ставит — под пар поля назначаете сами.
            </li>
          </ul>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={applying}
              onClick={() => setConfirm(false)}
            >
              Вернуться к раскладке
            </Button>
            <Button
              type="button"
              disabled={applying}
              onClick={() => {
                setConfirm(false);
                onApply();
              }}
            >
              {applying ? 'Записываем…' : 'Записать в черновик'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
